import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Eye, EyeOff, ExternalLink, Plus, Trash2, Save, Pencil, Link2, QrCode } from "lucide-react";
import ZoneEditor from "@/components/ZoneEditor";
import MediaInput from "@/components/MediaInput";
import ExcelTools from "@/components/ExcelTools";
import AuditLogList from "@/components/AuditLogList";
import AnalyticsTab from "@/components/AnalyticsTab";
import TranslationsEditor from "@/components/TranslationsEditor";
import MediaTab from "./MediaTab";
import PresentationTab from "./PresentationTab";
import LocationMapTab from "./LocationMapTab";
import OverviewTab from "./OverviewTab";
import FieldsTab from "./FieldsTab";
import ThemePresetPicker from "./ThemePresetPicker";
import { applyThemePreset } from "@/lib/themePresets";
import { PanoramaAspectHint, PanoramaSceneFields, PanoramaHotspotsEditor } from "./panorama/PanoramaAdminHelpers";

const TABS = ["Overview", "Media", "Scenes", "Transitions", "Panoramas", "Presentation", "Location", "Etaps & Blocks", "Units", "Fields", "Zones", "Leads", "Analytics", "Publish", "Settings", "Audit"];

export default function AdminProjectDetail() {
  const { pid } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [tab, setTab] = useState("Overview");
  const [scenes, setScenes] = useState([]);
  const [transitions, setTransitions] = useState([]);
  const [zones, setZones] = useState([]);
  const [groups, setGroups] = useState([]);
  const [entities, setEntities] = useState([]);
  const [leads, setLeads] = useState([]);

  const loadAll = async () => {
    try {
      const [p, s, t, z, g, e, l] = await Promise.all([
        api.get(`/admin/projects/${pid}`),
        api.get(`/admin/projects/${pid}/scenes`),
        api.get(`/admin/projects/${pid}/transitions`),
        api.get(`/admin/projects/${pid}/zones`),
        api.get(`/admin/projects/${pid}/groups`),
        api.get(`/admin/projects/${pid}/entities`),
        api.get(`/client/projects/${pid}/leads`).catch(() => ({ data: [] })),
      ]);
      setProject(p.data); setScenes(s.data); setTransitions(t.data); setZones(z.data);
      setGroups(g.data); setEntities(e.data); setLeads(l.data);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [pid]);

  const publishToggle = async () => {
    try {
      if (project.is_published) {
        await api.post(`/admin/projects/${pid}/unpublish`);
        toast.success("Unpublished");
      } else {
        await api.post(`/admin/projects/${pid}/publish`);
        toast.success("Published");
      }
      loadAll();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  if (!project) {
    return <div className="p-8 text-sm text-zinc-500">Loading…</div>;
  }

  return (
    <div className="px-8 py-6 max-w-7xl">
      <button onClick={() => navigate("/studio/projects")} className="text-xs text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1" data-testid="back-to-projects">
        <ArrowLeft className="h-3 w-3" /> All projects
      </button>
      <div className="flex items-end justify-between mt-3 mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-zinc-500">Project</div>
          <h1 className="font-display text-3xl tracking-tight mt-1" data-testid="project-name">{project.name}</h1>
          <div className="text-xs text-zinc-500 mt-1">/{project.slug}</div>
        </div>
        <div className="flex items-center gap-2">
          {project.is_published && (
            <a href={`/p/${project.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-3 py-2 rounded-md border border-zinc-300 text-sm hover:bg-zinc-50" data-testid="open-public-link">
              <ExternalLink className="h-4 w-4" /> Public viewer
            </a>
          )}
          <button onClick={publishToggle} data-testid="toggle-publish-btn"
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm transition ${project.is_published ? "border border-zinc-300 hover:bg-zinc-50" : "bg-zinc-900 text-white hover:bg-zinc-700"}`}>
            {project.is_published ? <><EyeOff className="h-4 w-4" /> Unpublish</> : <><Eye className="h-4 w-4" /> Publish</>}
          </button>
        </div>
      </div>

      <div className="flex gap-1 border-b border-zinc-200 mb-6 overflow-x-auto" data-testid="project-tabs">
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)} data-testid={`tab-${tabSlug(t)}`}
            className={`px-4 py-2.5 text-sm whitespace-nowrap border-b-2 transition ${tab === t ? "border-zinc-900 text-zinc-900 font-medium" : "border-transparent text-zinc-500 hover:text-zinc-900"}`}>
            {t}
          </button>
        ))}
      </div>

      {tab === "Overview" && <OverviewTab project={project} scenes={scenes} groups={groups} entities={entities} leads={leads} pid={pid} onJumpTab={setTab} />}
      {tab === "Settings" && <SetupTab project={project} reload={loadAll} />}
      {tab === "Scenes" && <ScenesTab project={project} pid={pid} scenes={scenes.filter(s => s.type !== "panorama_360")} allScenes={scenes} reload={loadAll} sceneTypeFilter="non-panorama" />}
      {tab === "Panoramas" && <ScenesTab project={project} pid={pid} scenes={scenes.filter(s => s.type === "panorama_360")} allScenes={scenes} reload={loadAll} sceneTypeFilter="panorama" />}
      {tab === "Transitions" && <TransitionsTab project={project} pid={pid} scenes={scenes} transitions={transitions} reload={loadAll} />}
      {tab === "Zones" && <ZonesTab project={project} pid={pid} scenes={scenes} entities={entities} groups={groups} zones={zones} reload={loadAll} />}
      {tab === "Etaps & Blocks" && <GroupsTab project={project} pid={pid} groups={groups} reload={loadAll} />}
      {tab === "Units" && <EntitiesTab project={project} pid={pid} slug={project.slug} entities={entities} groups={groups} reload={loadAll} />}
      {tab === "Fields" && <FieldsTab project={project} pid={pid} />}
      {tab === "Presentation" && <PresentationTab pid={pid} scenes={scenes} groups={groups} entities={entities} />}
      {tab === "Location" && <LocationMapTab project={project} pid={pid} reload={loadAll} />}
      {tab === "Media" && <MediaTab pid={pid} />}
      {tab === "Publish" && <PublishTab project={project} />}
      {tab === "Leads" && <LeadsTab leads={leads} entities={entities} />}
      {tab === "Analytics" && <AnalyticsTab projectId={pid} endpointBase="/admin" />}
      {tab === "Audit" && <AuditLogList scope="admin" projectId={pid} />}
    </div>
  );
}

function tabSlug(t) {
  return t.toLowerCase().replace(/\s*&\s*/g, "-").replace(/\s+/g, "-");
}

// ----- Setup --------------------------------------------------------------
function SetupTab({ project, reload }) {
  const [form, setForm] = useState({
    name: project.name, description: project.description || "",
    cover_url: project.cover_url || "", logo_url: project.logo_url || "",
    background_audio_url: project.background_audio_url || "",
    background_audio_volume: project.background_audio_volume ?? 0.35,
    accent_color: project.accent_color || "#C4A47C", theme: project.theme || "luxury_dark",
    letterbox_treatment: project.letterbox_treatment || "blurred_backdrop",
    target_ratio: project.target_ratio || "",
    target_ratio_custom: project.target_ratio_custom || "",
    payment_plans: project.payment_plans || [],
    contact_config: project.contact_config || {},
    pdf_template: project.pdf_template || { show_price: true, show_contact: true, show_qr: true, disclaimer: "" },
    default_language: project.default_language || "en",
    supported_languages: project.supported_languages || ["en"],
    translations: project.translations || {},
  });
  const save = async () => {
    try {
      const payload = {
        ...form,
        target_ratio: form.target_ratio || null,
        target_ratio_custom: form.target_ratio === "custom" ? (parseFloat(form.target_ratio_custom) || null) : null,
      };
      await api.put(`/admin/projects/${project.id}`, payload);
      toast.success("Saved");
      reload();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };
  const setCC = (k, v) => setForm({ ...form, contact_config: { ...form.contact_config, [k]: v } });
  const setPdf = (k, v) => setForm({ ...form, pdf_template: { ...form.pdf_template, [k]: v } });
  const toggleLang = (lang) => {
    const has = form.supported_languages.includes(lang);
    const next = has ? form.supported_languages.filter(l => l !== lang) : [...form.supported_languages, lang];
    if (next.length === 0) return;
    setForm({ ...form, supported_languages: next, default_language: next.includes(form.default_language) ? form.default_language : next[0] });
  };
  const otherLangs = (form.supported_languages || []).filter(l => l !== form.default_language);

  return (
    <div className="grid md:grid-cols-2 gap-6 max-w-4xl">
      <div className="space-y-4">
        <Field label="Project name"><input className="i" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="setup-name" /></Field>
        <Field label="Description"><textarea className="i h-24" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="setup-description" /></Field>
        <Field label="Cover image"><MediaInput projectId={project.id} value={form.cover_url} onChange={(v) => setForm({ ...form, cover_url: v })} accept="image/*" testid="setup-cover" /></Field>
        <Field label="Logo"><MediaInput projectId={project.id} value={form.logo_url} onChange={(v) => setForm({ ...form, logo_url: v })} accept="image/*" testid="setup-logo" /></Field>
        <Field label="Background music (loops on Public Viewer)">
          <MediaInput projectId={project.id} value={form.background_audio_url} onChange={(v) => setForm({ ...form, background_audio_url: v })} accept="audio/*" testid="setup-background-audio" />
          {form.background_audio_url && (
            <div className="mt-2 flex items-center gap-2 text-xs text-zinc-600">
              <span className="w-16">Volume</span>
              <input type="range" min="0" max="1" step="0.05" value={form.background_audio_volume} onChange={(e) => setForm({ ...form, background_audio_volume: parseFloat(e.target.value) })} className="flex-1" data-testid="setup-background-audio-volume" />
              <span className="w-8 text-right tabular-nums">{Math.round(form.background_audio_volume * 100)}%</span>
            </div>
          )}
        </Field>
        <Field label="Accent color"><input type="color" className="h-10 w-20 rounded border border-zinc-300" value={form.accent_color} onChange={(e) => setForm({ ...form, accent_color: e.target.value })} /></Field>
        <ThemePresetPicker
          form={form}
          onApply={(id) => {
            const next = applyThemePreset(form, id);
            setForm(next);
            toast.success(`Preset applied — remember to save`);
          }}
        />
        <Field label="Letterbox treatment">
          <select
            value={form.letterbox_treatment}
            onChange={(e) => setForm({ ...form, letterbox_treatment: e.target.value })}
            className="i"
            data-testid="setup-letterbox"
          >
            <option value="blurred_backdrop">Blurred backdrop (premium · recommended)</option>
            <option value="brand_color">Brand color fill</option>
            <option value="black">Solid black</option>
          </select>
          <div className="text-[11px] text-zinc-500 mt-1.5 leading-relaxed">
            Fills the empty space around contained scene media so the viewer never shows hard black bars.
            Zone mapping is unaffected.
          </div>
        </Field>
        <Field label="Scene fit">
          <select
            value={form.viewer_fit_mode || "per_scene"}
            onChange={(e) => setForm({ ...form, viewer_fit_mode: e.target.value })}
            className="i"
            data-testid="setup-viewer-fit-mode"
          >
            <option value="cover">Cover · cinematic full-bleed (recommended)</option>
            <option value="contain">Contain · letterbox, preserve every pixel</option>
            <option value="per_scene">Per-scene · each scene picks its own (legacy)</option>
          </select>
          <div className="text-[11px] text-zinc-500 mt-1.5 leading-relaxed">
            Enforces a single fit across every scene so rotation transitions feel consistent.
            Use <strong>Cover</strong> for premium full-screen presentations (may crop the edges of
            scenes whose aspect ratio doesn&apos;t match the viewport). Use <strong>Contain</strong> when
            architectural detail in the corners is critical.
          </div>
        </Field>
        <Field label="Cinematic target ratio">
          <select
            value={form.target_ratio || ""}
            onChange={(e) => setForm({ ...form, target_ratio: e.target.value })}
            className="i"
            data-testid="setup-target-ratio"
          >
            <option value="">Off · fill the viewport (legacy)</option>
            <option value="16:9">16:9 · landscape cinematic (recommended)</option>
            <option value="21:9">21:9 · ultra-wide</option>
            <option value="4:3">4:3 · classic</option>
            <option value="1:1">1:1 · square</option>
            <option value="9:16">9:16 · vertical / mobile</option>
            <option value="custom">Custom…</option>
          </select>
          {form.target_ratio === "custom" && (
            <input
              className="i mt-2"
              type="number" step="0.01" min="0.1"
              placeholder="Width ÷ height, e.g. 2.4"
              value={form.target_ratio_custom}
              onChange={(e) => setForm({ ...form, target_ratio_custom: e.target.value })}
              data-testid="setup-target-ratio-custom"
            />
          )}
          <div className="text-[11px] text-zinc-500 mt-1.5 leading-relaxed">
            Locks every scene into one fixed-aspect stage (centered, letterboxed) so scenes never
            resize or crop-jump during transitions. Leave <strong>Off</strong> to keep the previous
            full-viewport behavior.
          </div>
        </Field>
        <div>
          <div className="text-xs text-zinc-700 mb-1.5">Languages</div>
          <div className="flex flex-wrap gap-2">
            {[["en", "English"], ["ar", "Arabic"], ["tr", "Turkish"], ["ru", "Russian"]].map(([code, label]) => {
              const on = form.supported_languages.includes(code);
              const def = form.default_language === code;
              return (
                <button key={code} onClick={() => toggleLang(code)} type="button" data-testid={`lang-toggle-${code}`}
                  className={`text-xs px-3 py-1.5 rounded-full border transition ${on ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-300 text-zinc-600 hover:bg-zinc-50"}`}>
                  {label}{def && on ? " · default" : ""}
                </button>
              );
            })}
          </div>
          <div className="mt-2">
            <label className="text-xs text-zinc-500 mr-2">Default:</label>
            <select value={form.default_language} onChange={(e) => setForm({ ...form, default_language: e.target.value })}
              className="text-xs px-2 py-1 rounded border border-zinc-300" data-testid="lang-default">
              {form.supported_languages.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
        </div>
        {otherLangs.length > 0 && (
          <TranslationsEditor
            translations={form.translations}
            onChange={(t) => setForm({ ...form, translations: t })}
            supported={otherLangs}
            keys={["name", "description"]}
            testidPrefix="proj-tr"
          />
        )}
      </div>
      <div className="space-y-4">
        <div className="text-sm font-medium">Contact</div>
        <Field label="WhatsApp number"><input className="i" value={form.contact_config.whatsapp || ""} onChange={(e) => setCC("whatsapp", e.target.value)} data-testid="setup-whatsapp" /></Field>
        <Field label="Phone"><input className="i" value={form.contact_config.phone || ""} onChange={(e) => setCC("phone", e.target.value)} /></Field>
        <Field label="Email"><input className="i" value={form.contact_config.email || ""} onChange={(e) => setCC("email", e.target.value)} /></Field>
        <Field label="WhatsApp message template"><textarea className="i h-20" value={form.contact_config.whatsapp_template || ""} onChange={(e) => setCC("whatsapp_template", e.target.value)} /></Field>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.contact_config.lead_form_enabled} onChange={(e) => setCC("lead_form_enabled", e.target.checked)} data-testid="setup-leadform-enabled" /> Enable lead form</label>
      </div>
      <div className="md:col-span-2 space-y-4">
        <div className="text-sm font-medium">Sales PDF template</div>
        <div className="text-xs text-zinc-500 -mt-2">Controls the branded unit offer PDF that sales reps download or share via WhatsApp.</div>
        <div className="flex flex-wrap gap-4">
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.pdf_template.show_price !== false} onChange={(e) => setPdf("show_price", e.target.checked)} data-testid="setup-pdf-price" /> Show price</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.pdf_template.show_contact !== false} onChange={(e) => setPdf("show_contact", e.target.checked)} data-testid="setup-pdf-contact" /> Show contact strip</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.pdf_template.show_qr !== false} onChange={(e) => setPdf("show_qr", e.target.checked)} data-testid="setup-pdf-qr" /> Show QR code</label>
        </div>
        <Field label="Disclaimer (shown at PDF footer)">
          <textarea className="i h-20" value={form.pdf_template.disclaimer || ""} onChange={(e) => setPdf("disclaimer", e.target.value)} placeholder="All information is indicative only and subject to change without notice." data-testid="setup-pdf-disclaimer" />
        </Field>
        <Field label="PDF accent colour (HEX, optional override)">
          <input className="i font-mono" value={form.pdf_template.accent_hex || ""} onChange={(e) => setPdf("accent_hex", e.target.value)} placeholder={form.accent_color || "#C4A47C"} data-testid="setup-pdf-accent" />
        </Field>
      </div>
      <div className="md:col-span-2 space-y-3">
        <div className="text-sm font-medium">Payment plans</div>
        <div className="text-xs text-zinc-500 -mt-1">Presets surfaced in the Public Viewer Unit Detail when a unit has a price. Mark one as default — the buyer sees it first.</div>
        <PaymentPlansEditor
          plans={form.payment_plans || []}
          onChange={(p) => setForm({ ...form, payment_plans: p })}
        />
      </div>
      <div className="md:col-span-2">
        <button onClick={save} className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-zinc-900 text-white text-sm hover:bg-zinc-700" data-testid="setup-save"><Save className="h-4 w-4" /> Save</button>
      </div>
      <style>{`.i { width:100%; padding: 0.5rem 0.75rem; border:1px solid #d4d4d8; border-radius: 6px; outline:none; background:#fff; } .i:focus { border-color: #18181b; }`}</style>
    </div>
  );
}
function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs text-zinc-700 mb-1.5">{label}</div>
      {children}
    </label>
  );
}

// ----- Scenes -------------------------------------------------------------
function ScenesTab({ project, pid, scenes, reload, allScenes, sceneTypeFilter }) {
  const isPanoramaTab = sceneTypeFilter === "panorama";
  const empty = {
    project_id: pid,
    name: "",
    type: isPanoramaTab ? "panorama_360" : "static_image",
    media_url: "",
    poster_url: "",
    display_mode: "cover",
    loop_enabled: !isPanoramaTab,
    is_default: false,
    sort_order: scenes.length,
    panorama_initial_yaw: 0,
    panorama_initial_pitch: 0,
    panorama_initial_fov: 60,
    panorama_category: isPanoramaTab ? "project" : null,
  };
  const [form, setForm] = useState(null);
  const [batchPosters, setBatchPosters] = useState({ running: false, summary: null });
  const sceneListForHotspots = allScenes || scenes;

  const save = async () => {
    try {
      if (form.id) await api.put(`/admin/scenes/${form.id}`, form);
      else await api.post("/admin/scenes", form);
      toast.success("Saved");
      setForm(null);
      reload();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };
  const del = async (id) => {
    if (!confirm("Delete scene?")) return;
    await api.delete(`/admin/scenes/${id}`); reload();
  };
  // Eligible = video scenes (loop/scrubbable/transition) that have media.
  // We expose two batch modes:
  //   - default: fill in missing posters (most common; safe to repeat)
  //   - overwrite=true: re-derive even scenes that already have a poster
  const videoTypes = new Set(["loop_video", "scrubbable", "transition"]);
  const videoScenes = scenes.filter(s => videoTypes.has(s.type) && s.media_url);
  const missingPoster = videoScenes.filter(s => !s.poster_url);
  const runBatchPosters = async (overwrite) => {
    if (videoScenes.length === 0) { toast.error("No video scenes to derive posters from"); return; }
    if (overwrite && !confirm(`Re-derive posters for all ${videoScenes.length} video scenes? Existing posters will be replaced.`)) return;
    setBatchPosters({ running: true, summary: null });
    try {
      const r = await api.post(`/admin/projects/${pid}/derive-all-posters?overwrite=${overwrite}`);
      setBatchPosters({ running: false, summary: r.data });
      const { derived, skipped, failed } = r.data;
      if (failed > 0) toast.error(`Derived ${derived} · skipped ${skipped} · failed ${failed}`);
      else toast.success(`Derived ${derived} · skipped ${skipped}`);
      reload();
    } catch (e) {
      setBatchPosters({ running: false, summary: null });
      toast.error(apiErrorMessage(e));
    }
  };

  return (
    <div className="max-w-5xl">
      <div className="flex justify-end gap-2 mb-4 flex-wrap">
        {!isPanoramaTab && videoScenes.length > 0 && (
          <>
            <button
              onClick={() => runBatchPosters(false)}
              disabled={batchPosters.running || missingPoster.length === 0}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-zinc-300 bg-white text-sm hover:bg-zinc-50 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="generate-all-posters-btn"
              title={missingPoster.length === 0 ? "All video scenes already have posters" : `Generate posters for ${missingPoster.length} scene(s) without one`}
            >
              {batchPosters.running ? "Generating…" : `✨ Generate posters (${missingPoster.length})`}
            </button>
            <button
              onClick={() => runBatchPosters(true)}
              disabled={batchPosters.running}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-zinc-300 bg-white text-sm hover:bg-zinc-50 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="regenerate-all-posters-btn"
              title={`Re-derive all ${videoScenes.length} video scene posters`}
            >
              Re-derive all
            </button>
          </>
        )}
        <button onClick={() => setForm({ ...empty })} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-zinc-900 text-white text-sm" data-testid="add-scene-btn"><Plus className="h-4 w-4" /> Add scene</button>
      </div>

      {batchPosters.summary && (
        <div className="mb-4 rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-xs text-zinc-700" data-testid="batch-posters-summary">
          <div className="font-medium text-zinc-900 mb-1">Poster batch summary</div>
          <div>
            <span className="text-emerald-700">{batchPosters.summary.derived} derived</span>
            {" · "}
            <span className="text-zinc-600">{batchPosters.summary.skipped} skipped</span>
            {batchPosters.summary.failed > 0 && <> · <span className="text-red-700">{batchPosters.summary.failed} failed</span></>}
          </div>
          {batchPosters.summary.failed > 0 && (
            <ul className="mt-1.5 list-disc list-inside text-red-700">
              {batchPosters.summary.results.filter(r => r.status === "failed").map(r => (
                <li key={r.id}>{r.name}: {r.reason}</li>
              ))}
            </ul>
          )}
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-4">
        {scenes.length === 0 && <div className="text-sm text-zinc-500" data-testid="empty-scenes">No scenes yet.</div>}
        {scenes.map(s => {
          const isVideo = ["loop_video", "scrubbable", "transition"].includes(s.type);
          const isPano = s.type === "panorama_360";
          const thumbUrl = isVideo ? (s.poster_url || null) : (s.media_url || null);
          return (
          <div key={s.id} className="rounded-xl border border-zinc-200 bg-white overflow-hidden" data-testid={`scene-card-${s.id}`}>
            <div className="aspect-[16/9] bg-zinc-100 relative">
              {thumbUrl ? (
                <img src={thumbUrl} alt="" className="w-full h-full object-cover"
                  onError={(e) => { e.currentTarget.style.display = "none"; }} />
              ) : isVideo && s.media_url ? (
                // Video scene without a poster — render a muted <video> at the
                // 1st frame so admins still see *something* meaningful.
                <video src={s.media_url} muted playsInline preload="metadata"
                  className="w-full h-full object-cover pointer-events-none" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-zinc-400 text-[10px] uppercase tracking-wider">
                  {isVideo ? "Video · no poster" : isPano ? "360° panorama" : "No media"}
                </div>
              )}
              {s.is_default && <span className="absolute top-2 left-2 text-[10px] uppercase tracking-wider px-2 py-1 rounded bg-[#C4A47C] text-zinc-900">Default</span>}
              {isVideo && <span className="absolute top-2 right-2 text-[10px] uppercase tracking-wider px-2 py-1 rounded bg-black/70 text-white">Video</span>}
              {isPano && <span className="absolute top-2 right-2 text-[10px] uppercase tracking-wider px-2 py-1 rounded bg-black/70 text-white">360°</span>}
            </div>
            <div className="p-4">
              <div className="font-medium">{s.name}</div>
              <div className="text-xs text-zinc-500 mt-0.5">{s.type} · {s.display_mode}</div>
              <div className="mt-3 flex gap-2">
                <button onClick={() => setForm(s)} className="p-1.5 hover:bg-zinc-100 rounded text-zinc-600" data-testid={`edit-scene-${s.id}`}><Pencil className="h-4 w-4" /></button>
                <button onClick={() => del(s.id)} className="p-1.5 hover:bg-red-50 rounded text-red-600" data-testid={`del-scene-${s.id}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          </div>
          );
        })}
      </div>

      {form && (
        <Modal onClose={() => setForm(null)} title={form.id ? "Edit scene" : "Add scene"} testId="scene-form-modal">
          <div className="space-y-3">
            <Field label="Name"><input className="i" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="scene-name" /></Field>
            <TranslationsEditor translations={form.translations || {}} onChange={translations => setForm({ ...form, translations })} supported={(project.supported_languages || ["en"]).filter(l => l !== (project.default_language || "en"))} keys={["name"]} testidPrefix="scene-tr" />
            <Field label="Type">
              <select className="i" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="scene-type">
                <option value="static_image">Static image</option>
                <option value="loop_video">Loop video</option>
                <option value="transition">Transition video</option>
                <option value="scrubbable">Scrubbable</option>
                <option value="panorama_360">Panorama 360</option>
              </select>
            </Field>
            <Field label={form.type === "panorama_360" ? "Equirectangular 360 image (2:1)" : "Media (image / video)"}>
              <MediaInput projectId={pid} value={form.media_url} onChange={(v) => setForm({ ...form, media_url: v })} testid="scene-media" />
              {form.type === "panorama_360" && form.media_url && (
                <PanoramaAspectHint url={form.media_url} />
              )}
            </Field>
            <Field label="Poster (optional)">
              <MediaInput projectId={pid} value={form.poster_url || ""} onChange={(v) => setForm({ ...form, poster_url: v })} accept="image/*" testid="scene-poster" />
              {form.id && (form.type === "loop_video" || form.type === "scrubbable" || form.type === "transition") && form.media_url && (
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const r = await api.post(`/admin/scenes/${form.id}/derive-poster`);
                      setForm({ ...form, poster_url: r.data.poster_url });
                      toast.success("Poster generated from video");
                    } catch (e) { toast.error(apiErrorMessage(e)); }
                  }}
                  className="mt-2 text-xs px-3 py-1.5 rounded-md border border-zinc-300 bg-white hover:bg-zinc-50 inline-flex items-center gap-1.5"
                  data-testid={`scene-derive-poster-${form.id}`}
                >
                  ✨ Generate from video
                </button>
              )}
            </Field>
            <Field label="Display mode">
              <select className="i" value={form.display_mode} onChange={(e) => setForm({ ...form, display_mode: e.target.value })} data-testid="scene-display">
                <option value="cover">Cover (cinematic)</option>
                <option value="contain">Contain (precise zones)</option>
              </select>
            </Field>
            {form.type === "panorama_360" && (
              <PanoramaSceneFields form={form} setForm={setForm} pid={pid} />
            )}
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_default} onChange={(e) => setForm({ ...form, is_default: e.target.checked })} data-testid="scene-default" /> Default scene</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.loop_enabled} onChange={(e) => setForm({ ...form, loop_enabled: e.target.checked })} /> Loop enabled</label>
            <Field label="Background music override (optional · overrides project ambient while this scene is active)">
              <MediaInput projectId={pid} value={form.background_audio_url || ""} onChange={(v) => setForm({ ...form, background_audio_url: v || null })} accept="audio/*" testid="scene-background-audio" />
              {form.background_audio_url && (
                <div className="mt-2 flex items-center gap-2 text-xs text-zinc-600">
                  <span className="w-16">Volume</span>
                  <input type="range" min="0" max="1" step="0.05" value={form.background_audio_volume ?? 0.5} onChange={(e) => setForm({ ...form, background_audio_volume: parseFloat(e.target.value) })} className="flex-1" data-testid="scene-background-audio-volume" />
                  <span className="w-8 text-right tabular-nums">{Math.round((form.background_audio_volume ?? 0.5) * 100)}%</span>
                </div>
              )}
            </Field>
          </div>
          {form.type === "panorama_360" && form.id && (
            <PanoramaHotspotsEditor sceneId={form.id} projectId={pid} scenes={sceneListForHotspots} />
          )}
          <ModalActions onCancel={() => setForm(null)} onSave={save} testId="scene-save" />
          <style>{`.i { width:100%; padding: 0.5rem 0.75rem; border:1px solid #d4d4d8; border-radius: 6px; outline:none; background:#fff; }`}</style>
        </Modal>
      )}
    </div>
  );
}

// ----- Transitions --------------------------------------------------------
function TransitionsTab({ project, pid, scenes, transitions, reload }) {
  const empty = { project_id: pid, from_scene_id: "", to_scene_id: "", transition_type: "fade", transition_media_url: "", fallback_effect: "fade", scrubbable_enabled: false, swipe_direction: "right", button_label: "" };
  const [form, setForm] = useState(null);
  const sceneName = (id) => scenes.find(s => s.id === id)?.name || "—";

  const save = async () => {
    try {
      if (form.id) await api.put(`/admin/transitions/${form.id}`, form);
      else await api.post("/admin/transitions", form);
      toast.success("Saved");
      setForm(null);
      reload();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };
  const del = async (id) => { if (!confirm("Delete?")) return; await api.delete(`/admin/transitions/${id}`); reload(); };
  const prebake = async (id) => {
    try {
      await api.post(`/admin/transitions/${id}/prebake`);
      toast.success("Pre-bake queued");
      // Poll every 2s for up to 2 minutes until status terminal
      let tries = 0;
      const poll = setInterval(async () => {
        tries += 1;
        try {
          const r = await api.get(`/admin/projects/${pid}/transitions`);
          const t = r.data.find(x => x.id === id);
          if (!t) { clearInterval(poll); return; }
          if (t.prebake_status === "ready") {
            clearInterval(poll);
            toast.success(`Pre-bake ready · ${t.image_sequence_urls?.length || 0} frames`);
            reload();
          } else if (t.prebake_status === "failed") {
            clearInterval(poll);
            toast.error(`Pre-bake failed: ${t.image_sequence_error || "unknown"}`);
            reload();
          }
        } catch (_) { /* ignore poll error */ }
        if (tries >= 60) { clearInterval(poll); reload(); }
      }, 2000);
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };
  const clearPrebake = async (id) => {
    if (!confirm("Clear pre-baked frames?")) return;
    try {
      await api.delete(`/admin/transitions/${id}/prebake`);
      toast.success("Pre-bake cleared");
      reload();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  return (
    <div className="max-w-5xl">
      <div className="flex justify-end mb-4">
        <button onClick={() => setForm({ ...empty })} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-zinc-900 text-white text-sm" data-testid="add-transition-btn"><Plus className="h-4 w-4" /> Add transition</button>
      </div>
      <div className="rounded-xl border border-zinc-200 bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50/60 text-xs uppercase tracking-wider text-zinc-500">
            <tr><th className="text-left px-5 py-3">From → To</th><th className="text-left px-5 py-3">Type</th><th className="text-left px-5 py-3">Direction</th><th className="text-left px-5 py-3">Label</th><th className="text-left px-5 py-3">Pre-bake</th><th></th></tr>
          </thead>
          <tbody>
            {transitions.length === 0 && <tr><td colSpan="6" className="px-5 py-10 text-center text-zinc-500" data-testid="empty-transitions">No transitions yet.</td></tr>}
            {transitions.map(t => (
              <tr key={t.id} className="border-t border-zinc-100" data-testid={`transition-row-${t.id}`}>
                <td className="px-5 py-3">{sceneName(t.from_scene_id)} → {sceneName(t.to_scene_id)}</td>
                <td className="px-5 py-3">{t.transition_type}{t.scrubbable_enabled ? " (scrub)" : ""}</td>
                <td className="px-5 py-3">{t.swipe_direction}</td>
                <td className="px-5 py-3">{t.button_label || "—"}</td>
                <td className="px-5 py-3">
                  <PrebakeCell t={t} onPrebake={prebake} onClear={clearPrebake} />
                </td>
                <td className="px-5 py-3 text-right">
                  <button onClick={() => setForm(t)} className="p-1.5 hover:bg-zinc-100 rounded"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => del(t.id)} className="p-1.5 hover:bg-red-50 rounded text-red-600"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {form && (
        <Modal onClose={() => setForm(null)} title={form.id ? "Edit transition" : "Add transition"} testId="transition-form-modal">
          <div className="grid grid-cols-2 gap-3">
            <Field label="From scene">
              <select className="i" value={form.from_scene_id} onChange={(e) => setForm({ ...form, from_scene_id: e.target.value })} data-testid="trans-from">
                <option value="">—</option>{scenes.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </Field>
            <Field label="To scene">
              <select className="i" value={form.to_scene_id} onChange={(e) => setForm({ ...form, to_scene_id: e.target.value })} data-testid="trans-to">
                <option value="">—</option>{scenes.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </Field>
            <Field label="Type">
              <select className="i" value={form.transition_type} onChange={(e) => setForm({ ...form, transition_type: e.target.value })} data-testid="trans-type">
                <option value="fade">Fade · instant cross-fade (no video)</option>
                <option value="crossfade">Crossfade</option>
                <option value="cut">Cut · hard switch</option>
                <option value="auto_video">Auto video · plays media on swipe</option>
                <option value="scrubbable">Scrubbable · user drags to scrub</option>
              </select>
            </Field>
            <Field label="Swipe direction">
              <select className="i" value={form.swipe_direction} onChange={(e) => setForm({ ...form, swipe_direction: e.target.value })} data-testid="trans-direction">
                <option value="left">Left</option><option value="right">Right</option><option value="up">Up</option><option value="down">Down</option><option value="none">None</option>
              </select>
            </Field>
            <Field label="Transition media (optional)"><MediaInput projectId={pid} value={form.transition_media_url || ""} onChange={(v) => setForm({ ...form, transition_media_url: v })} testid="trans-media" /></Field>
            <Field label="Button label (optional)"><input className="i" value={form.button_label || ""} onChange={(e) => setForm({ ...form, button_label: e.target.value })} /></Field>
            <div className="col-span-2"><TranslationsEditor translations={form.translations || {}} onChange={translations => setForm({ ...form, translations })} supported={(project.supported_languages || ["en"]).filter(l => l !== (project.default_language || "en"))} keys={["button_label"]} testidPrefix="transition-tr" /></div>
            <label className="flex items-center gap-2 text-sm col-span-2"><input type="checkbox" checked={form.scrubbable_enabled} onChange={(e) => setForm({ ...form, scrubbable_enabled: e.target.checked })} data-testid="trans-scrubbable" /> Scrubbable enabled</label>
            <Field label="Return trip (B → A)">
              <select
                className="i"
                value={form.reverse_mode || "one_way"}
                onChange={(e) => setForm({ ...form, reverse_mode: e.target.value })}
                data-testid="trans-reverse-mode"
                disabled={!form.transition_media_url}
              >
                <option value="one_way">One way only · no automatic reverse</option>
                <option value="same_media">Use this same video, played reversed</option>
                <option value="separate_media">Separate media · I&apos;ll add a second transition row</option>
              </select>
              <div className="text-[11px] text-zinc-500 mt-1.5 leading-relaxed">
                When set to <strong>Use this same video reversed</strong>, the viewer synthesizes the
                B→A return trip from this single upload — works for both Scrubbable and Auto-video.
                Disabled until you set a Transition media URL.
              </div>
            </Field>
          </div>
          <ModalActions onCancel={() => setForm(null)} onSave={save} testId="trans-save" />
          <style>{`.i { width:100%; padding: 0.5rem 0.75rem; border:1px solid #d4d4d8; border-radius: 6px; outline:none; background:#fff; }`}</style>
        </Modal>
      )}
    </div>
  );
}

// ----- Zones --------------------------------------------------------------
function ZonesTab({ project, pid, scenes, entities, groups, zones, reload }) {
  const [sceneId, setSceneId] = useState(scenes[0]?.id || "");
  useEffect(() => { if (!sceneId && scenes[0]) setSceneId(scenes[0].id); }, [scenes, sceneId]);
  const scene = scenes.find(s => s.id === sceneId);
  const sceneZones = zones.filter(z => z.scene_id === sceneId);

  return (
    <div className="max-w-6xl">
      <div className="mb-4">
        <label className="text-xs text-zinc-700 mr-2">Scene:</label>
        <select value={sceneId} onChange={(e) => setSceneId(e.target.value)} className="px-3 py-1.5 rounded-md border border-zinc-300 text-sm" data-testid="zone-scene-select">
          {scenes.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>
      {scene ? (
        <ZoneEditor
          project={project}
          scene={scene}
          zones={sceneZones}
          entities={entities}
          groups={groups}
          scenes={scenes}
          pid={pid}
          onChange={reload}
        />
      ) : (
        <div className="text-sm text-zinc-500" data-testid="zone-no-scene">Create a scene first.</div>
      )}
    </div>
  );
}

// ----- Groups / Hierarchy ------------------------------------------------
function GroupsTab({ project, pid, groups, reload }) {
  const etapEmpty = { project_id: pid, group_type: "etap", parent_group_id: null, name: "", code: "", description: "", sort_order: 0, visibility: true };
  const blockEmpty = { project_id: pid, group_type: "block", parent_group_id: null, name: "", code: "", description: "", sort_order: 0, visibility: true };
  const [form, setForm] = useState(null);

  const etaps = useMemo(
    () => groups.filter((g) => g.group_type === "etap").sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)),
    [groups],
  );
  const blocksByEtap = useMemo(() => {
    const m = new Map();
    for (const g of groups) {
      if (g.group_type !== "block") continue;
      const arr = m.get(g.parent_group_id || "_orphan") || [];
      arr.push(g);
      m.set(g.parent_group_id || "_orphan", arr);
    }
    for (const arr of m.values()) arr.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    return m;
  }, [groups]);
  const orphanBlocks = blocksByEtap.get("_orphan") || [];

  const save = async () => {
    try {
      const payload = { ...form };
      // Mirror legacy `type` for back-compat with old viewer payloads.
      payload.type = payload.group_type;
      if (payload.group_type === "etap") payload.parent_group_id = null;
      if (!payload.code) payload.code = (payload.name || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "") || "g";
      if (form.id) await api.put(`/admin/groups/${form.id}`, payload);
      else await api.post("/admin/groups", payload);
      toast.success("Saved");
      setForm(null);
      reload();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };
  const del = async (id) => { if (!confirm("Delete group? Its units will be orphaned.")) return; await api.delete(`/admin/groups/${id}`); reload(); };

  return (
    <div className="max-w-4xl">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-sm font-medium text-zinc-900">Real-estate hierarchy</div>
          <div className="text-xs text-zinc-500 mt-0.5">Project → Etap → Block → Unit · stable codes identify each level on import.</div>
        </div>
        <button
          onClick={() => setForm({ ...etapEmpty, sort_order: etaps.length })}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-zinc-900 text-white text-sm"
          data-testid="add-etap-btn"
        >
          <Plus className="h-4 w-4" /> Add Etap
        </button>
      </div>

      {etaps.length === 0 && orphanBlocks.length === 0 && (
        <div className="rounded-xl border border-dashed border-zinc-300 p-10 text-center text-sm text-zinc-500" data-testid="empty-hierarchy">
          No Etaps yet — add one above, or import an Excel file to bootstrap the hierarchy.
        </div>
      )}

      <div className="space-y-4">
        {etaps.map((etap) => {
          const blocks = blocksByEtap.get(etap.id) || [];
          return (
            <div key={etap.id} className="rounded-xl border border-zinc-200 bg-white overflow-hidden" data-testid={`etap-card-${etap.id}`}>
              <div className="flex items-center justify-between px-5 py-3 bg-indigo-50/40 border-b border-zinc-200">
                <div className="flex items-baseline gap-3">
                  <span className="text-[10px] uppercase tracking-[0.25em] text-indigo-700 font-medium">Etap</span>
                  <span className="font-mono text-xs px-2 py-0.5 rounded bg-white border border-indigo-200 text-indigo-900" data-testid={`etap-code-${etap.id}`}>{etap.code || "—"}</span>
                  <div className="font-medium" data-testid={`etap-name-${etap.id}`}>{etap.name}</div>
                  <span className="text-[11px] text-zinc-500">order {etap.sort_order || 0}</span>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setForm({ ...blockEmpty, parent_group_id: etap.id, sort_order: blocks.length })}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-zinc-300 bg-white text-xs hover:bg-zinc-50"
                    data-testid={`add-block-to-${etap.id}`}
                  >
                    <Plus className="h-3 w-3" /> Add Block
                  </button>
                  <button onClick={() => setForm(etap)} className="p-1.5 hover:bg-zinc-100 rounded" data-testid={`edit-etap-${etap.id}`}><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => del(etap.id)} className="p-1.5 hover:bg-red-50 rounded text-red-600" data-testid={`del-etap-${etap.id}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              {blocks.length === 0 ? (
                <div className="px-5 py-4 text-xs text-zinc-500" data-testid={`empty-blocks-${etap.id}`}>No blocks yet. Add one above or via Excel import.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50/60 text-[11px] uppercase tracking-wider text-zinc-500">
                    <tr><th className="text-left px-5 py-2 w-24">Code</th><th className="text-left px-5 py-2">Name</th><th className="text-left px-5 py-2 w-20">Order</th><th className="text-left px-5 py-2">Description</th><th></th></tr>
                  </thead>
                  <tbody>
                    {blocks.map((b) => (
                      <tr key={b.id} className="border-t border-zinc-100" data-testid={`block-row-${b.id}`}>
                        <td className="px-5 py-2 font-mono text-xs">{b.code || "—"}</td>
                        <td className="px-5 py-2 font-medium">{b.name}</td>
                        <td className="px-5 py-2 text-zinc-600">{b.sort_order || 0}</td>
                        <td className="px-5 py-2 text-zinc-600">{b.description}</td>
                        <td className="px-5 py-2 text-right whitespace-nowrap">
                          <button onClick={() => setForm(b)} className="p-1.5 hover:bg-zinc-100 rounded" data-testid={`edit-block-${b.id}`}><Pencil className="h-4 w-4" /></button>
                          <button onClick={() => del(b.id)} className="p-1.5 hover:bg-red-50 rounded text-red-600" data-testid={`del-block-${b.id}`}><Trash2 className="h-4 w-4" /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          );
        })}

        {orphanBlocks.length > 0 && (
          <div className="rounded-xl border border-amber-200 bg-amber-50/30 overflow-hidden">
            <div className="px-5 py-3 border-b border-amber-200">
              <div className="text-xs font-medium text-amber-900">Unparented blocks</div>
              <div className="text-[11px] text-amber-800 mt-0.5">Legacy blocks with no Etap. Edit each to assign a parent Etap.</div>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-amber-50 text-[11px] uppercase tracking-wider text-amber-900">
                <tr><th className="text-left px-5 py-2 w-24">Code</th><th className="text-left px-5 py-2">Name</th><th></th></tr>
              </thead>
              <tbody>
                {orphanBlocks.map((b) => (
                  <tr key={b.id} className="border-t border-amber-100" data-testid={`orphan-block-${b.id}`}>
                    <td className="px-5 py-2 font-mono text-xs">{b.code || "—"}</td>
                    <td className="px-5 py-2 font-medium">{b.name}</td>
                    <td className="px-5 py-2 text-right">
                      <button onClick={() => setForm(b)} className="p-1.5 hover:bg-zinc-100 rounded"><Pencil className="h-4 w-4" /></button>
                      <button onClick={() => del(b.id)} className="p-1.5 hover:bg-red-50 rounded text-red-600"><Trash2 className="h-4 w-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {form && (
        <Modal onClose={() => setForm(null)} title={form.id ? `Edit ${form.group_type || "group"}` : `Add ${form.group_type || "group"}`} testId="group-form-modal">
          <div className="space-y-3">
            <Field label="Type">
              <select
                className="i"
                value={form.group_type || "block"}
                onChange={(e) => setForm({ ...form, group_type: e.target.value, parent_group_id: e.target.value === "etap" ? null : form.parent_group_id })}
                data-testid="group-type"
                disabled={!!form.id}
              >
                <option value="etap">Etap (phase)</option>
                <option value="block">Block</option>
                <option value="section">Section</option>
                <option value="custom">Custom</option>
              </select>
              {form.id && <div className="text-[11px] text-zinc-500 mt-1.5">Type is locked after creation.</div>}
            </Field>
            {form.group_type !== "etap" && (
              <Field label="Parent Etap">
                <select
                  className="i"
                  value={form.parent_group_id || ""}
                  onChange={(e) => setForm({ ...form, parent_group_id: e.target.value || null })}
                  data-testid="group-parent"
                >
                  <option value="">— Unparented —</option>
                  {etaps.map((e) => (
                    <option key={e.id} value={e.id}>{e.name} ({e.code})</option>
                  ))}
                </select>
              </Field>
            )}
            <Field label="Code">
              <input className="i font-mono" value={form.code || ""} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="e.g. ETAP-1, A, B-2" data-testid="group-code" />
              <div className="text-[11px] text-zinc-500 mt-1.5">Stable identifier for Excel import/export. Survives renames.</div>
            </Field>
            <Field label="Name"><input className="i" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="group-name" /></Field>
            <Field label="Display order"><input type="number" className="i" value={form.sort_order || 0} onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value || "0", 10) })} data-testid="group-sort" /></Field>
            <Field label="Description"><textarea className="i h-20" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
            {form.group_type === "block" && (
              <>
                <Field label="Selection mode (Public Viewer)">
                  <select className="i" value={form.selection_mode || ""} onChange={(e) => setForm({ ...form, selection_mode: e.target.value || null })} data-testid="block-selection-mode">
                    <option value="">Auto (facade grid if mapped, else floor stack)</option>
                    <option value="facade_grid">Facade grid only</option>
                    <option value="floor_stack">Floor stack only</option>
                    <option value="both">Both — facade grid + floor stack</option>
                    <option value="none">None — Units List only</option>
                  </select>
                  <div className="text-[11px] text-zinc-500 mt-1.5">
                    Floor stack shows a vertical building column. Requires units to have a numeric Floor value.
                  </div>
                </Field>
                <Field label="Floor numbering direction">
                  <select className="i" value={form.floor_direction || "highest_first"} onChange={(e) => setForm({ ...form, floor_direction: e.target.value })} data-testid="block-floor-direction">
                    <option value="highest_first">Top floor first (10 → 1)</option>
                    <option value="lowest_first">Ground floor first (1 → 10)</option>
                  </select>
                </Field>
              </>
            )}
            <TranslationsEditor
              translations={form.translations || {}}
              onChange={(t) => setForm({ ...form, translations: t })}
              supported={(project.supported_languages || ["en"]).filter(l => l !== (project.default_language || "en"))}
              keys={["name", "description"]}
              testidPrefix="group-tr"
            />
          </div>
          <ModalActions onCancel={() => setForm(null)} onSave={save} testId="group-save" />
          <style>{`.i { width:100%; padding: 0.5rem 0.75rem; border:1px solid #d4d4d8; border-radius: 6px; outline:none; background:#fff; }`}</style>
        </Modal>
      )}
    </div>
  );
}

// ----- Entities -----------------------------------------------------------
function EntitiesTab({ project, pid, slug, entities, groups, reload }) {
  const blocks = useMemo(() => groups.filter((g) => g.group_type === "block"), [groups]);
  const etaps = useMemo(() => groups.filter((g) => g.group_type === "etap"), [groups]);
  const etapById = useMemo(() => Object.fromEntries(etaps.map((e) => [e.id, e])), [etaps]);
  const groupById = useMemo(() => Object.fromEntries(groups.map((g) => [g.id, g])), [groups]);
  const blockPath = (b) => {
    if (!b) return "—";
    const etap = etapById[b.parent_group_id];
    return etap ? `${etap.name} › ${b.name}` : b.name;
  };

  const [filterEtap, setFilterEtap] = useState("");
  const [filterBlock, setFilterBlock] = useState("");
  const visibleBlocks = filterEtap ? blocks.filter((b) => b.parent_group_id === filterEtap) : blocks;
  const filteredEntities = entities.filter((e) => {
    const grp = groupById[e.group_id];
    if (filterBlock) return e.group_id === filterBlock;
    if (filterEtap) return grp && grp.parent_group_id === filterEtap;
    return true;
  });

  const empty = {
    project_id: pid, group_id: blocks[0]?.id || groups[0]?.id || "", type: "apartment",
    name: "", code: "", status: "available", visibility: true, sort_order: entities.length,
    floor: null, fields: {}, plan_url: "", gallery_urls: [], video_url: "", brochure_url: "", description: "",
    translations: {},
  };
  const [form, setForm] = useState(null);

  // Phase 1b — visual-location coverage (zones / facade / floor / 360 scene).
  const [coverage, setCoverage] = useState({});
  const [missingCount, setMissingCount] = useState(0);
  useEffect(() => {
    let alive = true;
    api.get(`/admin/projects/${pid}/unit-locations`).then((r) => {
      if (!alive) return;
      const map = {};
      (r.data.units || []).forEach((u) => { map[u.entity_id] = u; });
      setCoverage(map); setMissingCount(r.data.missing || 0);
    }).catch(() => {});
    return () => { alive = false; };
  }, [pid, entities.length]);

  const save = async () => {
    try {
      const body = { ...form };
      if (typeof body.gallery_urls === "string") {
        body.gallery_urls = body.gallery_urls.split(/\n+/).map(s => s.trim()).filter(Boolean);
      }
      if (body.floor === "" || body.floor === undefined) body.floor = null;
      if (body.id) await api.put(`/admin/entities/${body.id}`, body);
      else await api.post("/admin/entities", body);
      toast.success("Saved"); setForm(null); reload();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };
  const del = async (id) => { if (!confirm("Delete unit?")) return; await api.delete(`/admin/entities/${id}`); reload(); };

  return (
    <div className="max-w-6xl">
      <div className="flex justify-between mb-4 gap-2 flex-wrap items-center">
        <div className="flex gap-2 items-center">
          <select
            className="text-sm px-2.5 py-1.5 rounded-md border border-zinc-300"
            value={filterEtap}
            onChange={(e) => { setFilterEtap(e.target.value); setFilterBlock(""); }}
            data-testid="filter-etap"
          >
            <option value="">All Etaps</option>
            {etaps.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
          <select
            className="text-sm px-2.5 py-1.5 rounded-md border border-zinc-300"
            value={filterBlock}
            onChange={(e) => setFilterBlock(e.target.value)}
            data-testid="filter-block"
          >
            <option value="">All Blocks</option>
            {visibleBlocks.map((b) => <option key={b.id} value={b.id}>{blockPath(b)}</option>)}
          </select>
          <div className="text-xs text-zinc-500">{filteredEntities.length} of {entities.length}</div>
        </div>
        <div className="flex gap-2">
          <ExcelTools projectId={pid} onAfterImport={reload} />
          <button onClick={() => setForm({ ...empty })} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-zinc-900 text-white text-sm" data-testid="add-entity-btn"><Plus className="h-4 w-4" /> Add unit</button>
        </div>
      </div>
      <div className="rounded-xl border border-zinc-200 bg-white overflow-hidden">
        {missingCount > 0 && (
          <div className="px-5 py-3 bg-amber-50 border-b border-amber-200 text-amber-800 text-sm flex items-center gap-2" data-testid="unit-locations-warning">
            <span className="font-medium">{missingCount}</span> unit{missingCount === 1 ? "" : "s"} have no visual location yet (no zone, facade cell, floor, or linked scene). Buyers won't be able to "Locate" {missingCount === 1 ? "it" : "them"} in the viewer.
          </div>
        )}
        <table className="w-full text-sm">
          <thead className="bg-zinc-50/60 text-xs uppercase tracking-wider text-zinc-500">
            <tr><th className="text-left px-5 py-3">Code</th><th className="text-left px-5 py-3">Etap › Block</th><th className="text-left px-5 py-3">Floor</th><th className="text-left px-5 py-3">Type</th><th className="text-left px-5 py-3">Status</th><th className="text-left px-5 py-3">Visual location</th><th></th></tr>
          </thead>
          <tbody>
            {filteredEntities.length === 0 && <tr><td colSpan="7" className="px-5 py-10 text-center text-zinc-500" data-testid="empty-entities">No units yet.</td></tr>}
            {filteredEntities.map(e => (
              <tr key={e.id} className="border-t border-zinc-100" data-testid={`unit-row-${e.id}`}>
                <td className="px-5 py-3 font-mono text-xs">{e.code}</td>
                <td className="px-5 py-3 text-zinc-600">{blockPath(groupById[e.group_id])}</td>
                <td className="px-5 py-3 text-zinc-600">{e.floor ?? "—"}</td>
                <td className="px-5 py-3 text-zinc-600">{e.type}</td>
                <td className="px-5 py-3">
                  <span className={`text-xs px-2 py-1 rounded ${e.status === "available" ? "bg-emerald-100 text-emerald-800" : e.status === "reserved" ? "bg-amber-100 text-amber-800" : e.status === "sold" ? "bg-red-100 text-red-800" : "bg-zinc-100 text-zinc-600"}`}>
                    {e.status}
                  </span>
                </td>
                <td className="px-5 py-3" data-testid={`unit-loc-${e.id}`}>
                  {(() => {
                    const c = coverage[e.id];
                    if (!c) return <span className="text-zinc-300 text-xs">…</span>;
                    if (!c.has_any) return <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded bg-amber-100 text-amber-800 border border-amber-200" data-testid={`unit-loc-none-${e.id}`}>No location</span>;
                    const chips = [];
                    if (c.zone) chips.push(["Zone", "bg-sky-100 text-sky-800"]);
                    if (c.facade) chips.push(["Facade", "bg-violet-100 text-violet-800"]);
                    if (c.floor) chips.push(["Floor", "bg-emerald-100 text-emerald-800"]);
                    if (c.scene) chips.push(["360°", "bg-amber-100 text-amber-800"]);
                    return <span className="flex flex-wrap gap-1">{chips.map(([label, cls]) => <span key={label} className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${cls}`}>{label}</span>)}</span>;
                  })()}
                </td>
                <td className="px-5 py-3 text-right">
                  {slug && (
                    <>
                      <button
                        onClick={async () => {
                          const url = `${window.location.origin}/p/${slug}?unit=${encodeURIComponent(e.code)}`;
                          const { copyToClipboard } = await import("@/lib/share");
                          if (await copyToClipboard(url)) toast.success("Unit link copied");
                          else toast.error("Copy failed");
                        }}
                        className="p-1.5 hover:bg-zinc-100 rounded"
                        title="Copy direct link to this unit"
                        data-testid={`unit-copy-link-${e.code}`}
                      >
                        <Link2 className="h-4 w-4" />
                      </button>
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/public/projects/${slug}/qr?unit=${encodeURIComponent(e.code)}`}
                        download={`qr-${slug}-${e.code}.png`}
                        className="inline-flex p-1.5 hover:bg-zinc-100 rounded"
                        title="Download QR code"
                        data-testid={`unit-download-qr-${e.code}`}
                      >
                        <QrCode className="h-4 w-4" />
                      </a>
                    </>
                  )}
                  <button data-testid={`entity-edit-${e.code}`} onClick={() => setForm({ ...e, gallery_urls: (e.gallery_urls || []).join("\n") })} className="p-1.5 hover:bg-zinc-100 rounded"><Pencil className="h-4 w-4" /></button>
                  <button data-testid={`entity-delete-${e.code}`} onClick={() => del(e.id)} className="p-1.5 hover:bg-red-50 rounded text-red-600"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {form && (
        <Modal onClose={() => setForm(null)} title={form.id ? "Edit unit" : "Add unit"} wide testId="entity-form-modal">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Code"><input className="i" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} data-testid="entity-code" /></Field>
            <Field label="Name"><input className="i" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
            <Field label="Block (parent group)">
              <select className="i" value={form.group_id} onChange={(e) => setForm({ ...form, group_id: e.target.value })} data-testid="entity-block">
                <option value="">—</option>
                {blocks.map(b => <option key={b.id} value={b.id}>{blockPath(b)}</option>)}
              </select>
            </Field>
            <Field label="Floor"><input type="number" className="i" value={form.floor ?? ""} onChange={(e) => setForm({ ...form, floor: e.target.value === "" ? null : parseInt(e.target.value, 10) })} data-testid="entity-floor" /></Field>
            <Field label="Type"><input className="i" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} /></Field>
            <Field label="Status">
              <select className="i" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="entity-status">
                <option value="available">Available</option><option value="reserved">Reserved</option>
                <option value="sold">Sold</option><option value="hidden">Hidden</option>
                <option value="coming_soon">Coming soon</option><option value="featured">Featured</option>
              </select>
            </Field>
            <Field label="Plan"><MediaInput projectId={pid} value={form.plan_url || ""} onChange={(v) => setForm({ ...form, plan_url: v })} accept="image/*" preserveOriginal testid="entity-plan" /></Field>
            <Field label="Video"><MediaInput projectId={pid} value={form.video_url || ""} onChange={(v) => setForm({ ...form, video_url: v })} accept="video/*" testid="entity-video" /></Field>
            <Field label="Brochure"><MediaInput projectId={pid} value={form.brochure_url || ""} onChange={(v) => setForm({ ...form, brochure_url: v })} accept="application/pdf" testid="entity-brochure" /></Field>
            <Field label="View from this unit (image)"><MediaInput projectId={pid} value={form.view_url || ""} onChange={(v) => setForm({ ...form, view_url: v })} accept="image/*" testid="entity-view-image" /></Field>
            <Field label="View from this unit (video)"><MediaInput projectId={pid} value={form.view_video_url || ""} onChange={(v) => setForm({ ...form, view_video_url: v })} accept="video/*" testid="entity-view-video" /></Field>
            <Field label="View direction (e.g. North-East)"><input className="i" value={form.view_direction || ""} onChange={(e) => setForm({ ...form, view_direction: e.target.value })} data-testid="entity-view-direction" /></Field>
            <Field label="Scene→Plan transition video"><MediaInput projectId={pid} value={form.plan_transition_video_url || ""} onChange={(v) => setForm({ ...form, plan_transition_video_url: v })} accept="video/*" testid="entity-plan-transition" /></Field>
            <div className="col-span-2"><Field label="View description"><textarea className="i h-16" value={form.view_description || ""} onChange={(e) => setForm({ ...form, view_description: e.target.value })} data-testid="entity-view-description" /></Field></div>
            <div className="col-span-2"><Field label="Gallery URLs (one per line)"><textarea className="i h-20" value={form.gallery_urls} onChange={(e) => setForm({ ...form, gallery_urls: e.target.value })} /></Field></div>
            <div className="col-span-2"><Field label="Description"><textarea className="i h-20" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field></div>
            <div className="col-span-2">
              <div className="text-xs text-zinc-700 mb-1.5">Custom fields (key → value)</div>
              <FieldsEditor fields={form.fields || {}} onChange={(f) => setForm({ ...form, fields: f })} />
            </div>
            <div className="col-span-2">
              <TranslationsEditor
                translations={form.translations || {}}
                onChange={(t) => setForm({ ...form, translations: t })}
                supported={(project.supported_languages || ["en"]).filter(l => l !== (project.default_language || "en"))}
                keys={["name", "description"]}
                fields={form.fields || {}}
                testidPrefix="entity-tr"
              />
            </div>
          </div>
          <ModalActions onCancel={() => setForm(null)} onSave={save} testId="entity-save" />
          <style>{`.i { width:100%; padding: 0.5rem 0.75rem; border:1px solid #d4d4d8; border-radius: 6px; outline:none; background:#fff; }`}</style>
        </Modal>
      )}
    </div>
  );
}

function FieldsEditor({ fields, onChange }) {
  const entries = useMemo(() => Object.entries(fields), [fields]);
  const set = (i, key, val) => {
    const next = [...entries];
    next[i] = [key, val];
    onChange(Object.fromEntries(next));
  };
  const remove = (i) => {
    const next = entries.filter((_, idx) => idx !== i);
    onChange(Object.fromEntries(next));
  };
  const add = () => onChange({ ...fields, ["Field " + (entries.length + 1)]: "" });
  return (
    <div className="space-y-2">
      {entries.map(([k, v], i) => (
        <div key={i} className="flex gap-2">
          <input value={k} onChange={(e) => set(i, e.target.value, v)} className="i flex-1" placeholder="Label" />
          <input value={String(v)} onChange={(e) => set(i, k, e.target.value)} className="i flex-1" placeholder="Value" />
          <button onClick={() => remove(i)} className="p-2 text-red-600 hover:bg-red-50 rounded"><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
      <button onClick={add} className="text-xs text-zinc-600 hover:text-zinc-900 inline-flex items-center gap-1"><Plus className="h-3 w-3" /> Add field</button>
    </div>
  );
}

function TranslationsEditorLocal_REMOVED() { return null; }

// ----- Publish ------------------------------------------------------------
function PublishTab({ project }) {
  const url = `${window.location.origin}/p/${project.slug}`;
  const embed = `<iframe src="${window.location.origin}/embed/${project.slug}" style="width:100%; height:100vh; border:0;" allow="fullscreen; autoplay"></iframe>`;
  return (
    <div className="max-w-3xl space-y-6">
      <div className="rounded-xl border border-zinc-200 bg-white p-5">
        <div className="text-sm font-medium mb-2">Public URL</div>
        <div className="flex gap-2">
          <input readOnly value={url} className="flex-1 px-3 py-2 rounded-md border border-zinc-300 font-mono text-xs" data-testid="public-url" />
          <a href={url} target="_blank" rel="noreferrer" className="px-3 py-2 rounded-md bg-zinc-900 text-white text-sm inline-flex items-center gap-1"><ExternalLink className="h-4 w-4" /> Open</a>
        </div>
      </div>
      <div className="rounded-xl border border-zinc-200 bg-white p-5">
        <div className="text-sm font-medium mb-2">Iframe embed code</div>
        <textarea readOnly value={embed} className="w-full px-3 py-2 rounded-md border border-zinc-300 font-mono text-xs h-24" data-testid="embed-code" />
      </div>
      <div className="rounded-xl border border-zinc-200 bg-white p-5">
        <div className="text-sm font-medium mb-2">Status</div>
        <div className="text-xs text-zinc-600">{project.is_published ? `Published at ${project.published_at}` : "Draft — click Publish above to make this project available."}</div>
      </div>
    </div>
  );
}

// ----- Leads --------------------------------------------------------------
function LeadsTab({ leads, entities }) {
  return (
    <div className="max-w-5xl rounded-xl border border-zinc-200 bg-white overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-zinc-50/60 text-xs uppercase tracking-wider text-zinc-500">
          <tr><th className="text-left px-5 py-3">Name</th><th className="text-left px-5 py-3">Phone</th><th className="text-left px-5 py-3">Unit</th><th className="text-left px-5 py-3">Message</th><th className="text-left px-5 py-3">Date</th></tr>
        </thead>
        <tbody>
          {leads.length === 0 && <tr><td colSpan="5" className="px-5 py-10 text-center text-zinc-500" data-testid="empty-leads">No leads yet.</td></tr>}
          {leads.map(l => (
            <tr key={l.id} className="border-t border-zinc-100">
              <td className="px-5 py-3 font-medium">{l.name}</td>
              <td className="px-5 py-3">{l.phone}</td>
              <td className="px-5 py-3 text-zinc-600">{entities.find(e => e.id === l.entity_id)?.code || "—"}</td>
              <td className="px-5 py-3 text-zinc-600 max-w-md truncate">{l.message}</td>
              <td className="px-5 py-3 text-zinc-600">{new Date(l.created_at).toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ----- Pre-bake status cell ----------------------------------------------
function PrebakeCell({ t, onPrebake, onClear }) {
  if (!t.scrubbable_enabled || !t.transition_media_url) {
    return <span className="text-xs text-zinc-400">—</span>;
  }
  const status = t.prebake_status || "not_prepared";
  if (status === "processing") {
    return <span className="text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded" data-testid={`prebake-status-${t.id}`}>Processing…</span>;
  }
  if (status === "ready") {
    return (
      <div className="flex items-center gap-2" data-testid={`prebake-status-${t.id}`}>
        <span className="text-xs text-emerald-700 bg-emerald-50 px-2 py-1 rounded">Ready · {t.image_sequence_urls?.length || 0} frames</span>
        <button onClick={() => onClear(t.id)} className="text-xs text-zinc-500 hover:text-zinc-900 underline" data-testid={`prebake-clear-${t.id}`}>Clear</button>
      </div>
    );
  }
  if (status === "failed") {
    return (
      <div className="flex items-center gap-2" data-testid={`prebake-status-${t.id}`}>
        <span className="text-xs text-rose-700 bg-rose-50 px-2 py-1 rounded" title={t.image_sequence_error || ""}>Failed</span>
        <button onClick={() => onPrebake(t.id)} className="text-xs text-zinc-700 hover:text-zinc-900 underline" data-testid={`prebake-retry-${t.id}`}>Retry</button>
      </div>
    );
  }
  return (
    <button onClick={() => onPrebake(t.id)} className="text-xs px-2 py-1 rounded border border-zinc-300 hover:bg-zinc-50" data-testid={`prebake-trigger-${t.id}`}>
      Pre-bake
    </button>
  );
}

// ----- Modal helpers ------------------------------------------------------
function Modal({ children, onClose, title, wide, testId }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className={`bg-white rounded-xl p-6 w-full ${wide ? "max-w-3xl" : "max-w-lg"} max-h-[90vh] overflow-auto`} onClick={e => e.stopPropagation()} data-testid={testId}>
        <div className="font-display text-xl mb-5">{title}</div>
        {children}
      </div>
    </div>
  );
}
function ModalActions({ onCancel, onSave, testId }) {
  return (
    <div className="mt-6 flex justify-end gap-3">
      <button onClick={onCancel} className="px-4 py-2 rounded-md border border-zinc-300 text-sm">Cancel</button>
      <button onClick={onSave} className="px-4 py-2 rounded-md bg-zinc-900 text-white text-sm" data-testid={testId}>Save</button>
    </div>
  );
}

// ── Payment Plans inline editor (Phase B Feature 6) ──────────────────────
function PaymentPlansEditor({ plans, onChange }) {
  const update = (idx, patch) => onChange(plans.map((p, i) => (i === idx ? { ...p, ...patch } : p)));
  const add = () => onChange([...(plans || []), {
    id: `plan-${Date.now().toString(36)}`, name: "New plan", kind: "installment",
    down_payment_pct: 30, months: 24, discount_pct: 0, vat_pct: 0, currency: "USD",
    notes: "", default: plans.length === 0,
  }]);
  const remove = (idx) => onChange(plans.filter((_, i) => i !== idx));
  // Marking one as default un-marks the others — single source of truth.
  const markDefault = (idx) => onChange(plans.map((p, i) => ({ ...p, default: i === idx })));

  return (
    <div className="space-y-3" data-testid="payment-plans-editor">
      {plans.length === 0 && (
        <div className="rounded-lg border border-dashed border-zinc-300 p-6 text-center text-sm text-zinc-500" data-testid="empty-payment-plans">
          No payment plans yet. Add Cash / 12mo / 24mo presets — buyers see them in the Unit Detail when the unit has a price.
        </div>
      )}
      {plans.map((p, i) => (
        <div key={p.id || i} className="rounded-lg border border-zinc-200 bg-zinc-50/50 p-4" data-testid={`payment-plan-row-${i}`}>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2 items-end">
            <div className="md:col-span-2">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Name</div>
              <input className="i" value={p.name || ""} onChange={(e) => update(i, { name: e.target.value })} data-testid={`pp-name-${i}`} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Kind</div>
              <select className="i" value={p.kind || "installment"} onChange={(e) => update(i, { kind: e.target.value })} data-testid={`pp-kind-${i}`}>
                <option value="cash">Cash</option>
                <option value="installment">Installment</option>
                <option value="custom">Custom</option>
              </select>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">DP %</div>
              <input type="number" className="i" value={p.down_payment_pct ?? 0} onChange={(e) => update(i, { down_payment_pct: Number(e.target.value) })} data-testid={`pp-dp-${i}`} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Months</div>
              <input type="number" className="i" value={p.months ?? 0} onChange={(e) => update(i, { months: Number(e.target.value) })} data-testid={`pp-months-${i}`} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Discount %</div>
              <input type="number" className="i" value={p.discount_pct ?? 0} onChange={(e) => update(i, { discount_pct: Number(e.target.value) })} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">VAT %</div>
              <input type="number" className="i" value={p.vat_pct ?? 0} onChange={(e) => update(i, { vat_pct: Number(e.target.value) })} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Currency</div>
              <input className="i" value={p.currency || ""} onChange={(e) => update(i, { currency: e.target.value })} placeholder="USD" />
            </div>
            <div className="md:col-span-3">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Notes</div>
              <input className="i" value={p.notes || ""} onChange={(e) => update(i, { notes: e.target.value })} placeholder="e.g. 5% cash discount" />
            </div>
            <div className="flex items-center gap-2 mt-1">
              <label className="text-xs flex items-center gap-1.5 select-none">
                <input type="radio" name="pp-default" checked={!!p.default} onChange={() => markDefault(i)} data-testid={`pp-default-${i}`} /> Default
              </label>
              <button onClick={() => remove(i)} className="p-1.5 text-red-600 hover:bg-red-50 rounded ml-auto" title="Delete" data-testid={`pp-remove-${i}`}>
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
          <style>{`.i { width:100%; padding: 0.45rem 0.65rem; border:1px solid #d4d4d8; border-radius: 6px; outline:none; background:#fff; font-size:0.875rem; }`}</style>
        </div>
      ))}
      <button onClick={add} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-zinc-300 text-sm hover:bg-zinc-50" data-testid="add-payment-plan">
        <Plus className="h-3.5 w-3.5" /> Add payment plan
      </button>
    </div>
  );
}
